import React, { useState } from 'react';

import FnSLogo from '../../../assets/images/FnS_logo.png';
import FnSLogoThumbnail from '../../../assets/images/FnSPressKit/thumbnails/FnS_logo_thumbnail.png';

import PCGLogo from '../../../assets/images/PaperCactusLogo2.png';
import PCGLogoThumbnail from '../../../assets/images/FnSPressKit/thumbnails/PaperCactusLogo2_thumbnail.png';

import PCGLogoLong from '../../../assets/images/PCG-logo_long.png';
import PCGLogoLongThumbnail from '../../../assets/images/FnSPressKit/thumbnails/PCG-logo_long_thumbnail.png';

import './FnSpressKitLogos.css';

const FnSpressKitLogos = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState({});
    // Reset zoom and position when closing the viewer
  const handleClose = () => {
    setSelectedImage(null);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };
  // Handle keyboard events
  const handleKeyDown = (e) => {
    if (!selectedImage) return;
    
    switch (e.key) {
      case 'Escape':
        handleClose();
        break;
      case '+':
      case '=':
        setZoom(prevZoom => Math.min(3, prevZoom + 0.25));
        setPosition({ x: 0, y: 0 });
        break;
      case '-':
        setZoom(prevZoom => Math.max(1, prevZoom - 0.25));
        setPosition({ x: 0, y: 0 });
        break;
      default:
        break;
    }
  };  // Preload an image
  const preloadImage = (src) => {
    if (!preloadedImages[src]) {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        setPreloadedImages(prev => ({
          ...prev,
          [src]: true
        }));
      };
    }
  };

  // Handle image click to open the viewer
  const handleImageClick = (image) => {
    setIsLoading(true);
    setSelectedImage(image);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    
    // Preload adjacent images for smoother navigation
    const currentIndex = logoItems.findIndex(item => item.full === image.full);
    if (currentIndex > -1) {
      // Preload next image
      if (currentIndex < logoItems.length - 1) {
        preloadImage(logoItems[currentIndex + 1].full);
      }
      // Preload previous image
      if (currentIndex > 0) {
        preloadImage(logoItems[currentIndex - 1].full);
      }
    }
  };

  // Handle zoom with mouse wheel
  const handleWheel = (e) => {
    if (!selectedImage) return;
    
    e.preventDefault();
    const delta = e.deltaY * -0.01;
    const newZoom = Math.max(1, Math.min(3, zoom + delta));
    setZoom(newZoom);
  };
    // Handle click on the lightbox image to toggle zoom
  const handleImageToggleZoom = (e) => {
    e.stopPropagation();
    setZoom(zoom === 1 ? 2 : 1);
    setPosition({ x: 0, y: 0 });
  };
  // Handle mouse down for dragging
  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  // Handle touch start for mobile dragging
  const handleTouchStart = (e) => {
    if (zoom > 1 && e.touches && e.touches[0]) {
      setIsDragging(true);
      setDragStart({ 
        x: e.touches[0].clientX - position.x, 
        y: e.touches[0].clientY - position.y 
      });
    }
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      setPosition({ x: newX, y: newY });
    }
  };

  // Handle touch move for mobile dragging
  const handleTouchMove = (e) => {
    if (isDragging && zoom > 1 && e.touches && e.touches[0]) {
      e.preventDefault(); // Prevent scrolling while dragging
      const newX = e.touches[0].clientX - dragStart.x;
      const newY = e.touches[0].clientY - dragStart.y;
      setPosition({ x: newX, y: newY });
    }
  };

  // Handle mouse up to end dragging
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  // Handle touch end to end dragging on mobile
  const handleTouchEnd = () => {
    setIsDragging(false);
  };
  // Double-click to toggle between zoom 1 and zoom 3 (higher than single-click)
  const handleDoubleClick = () => {
    setZoom(zoom === 1 ? 3 : 1);
    setPosition({ x: 0, y: 0 });
  };

  // Download the full-size image
  const handleDownload = () => {
    if (selectedImage) {
      const link = document.createElement('a');
      link.href = selectedImage.full;
      link.download = selectedImage.alt || 'logo';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const logoItems = [
    { 
      thumbnail: FnSLogoThumbnail || FnSLogo, 
      full: FnSLogo, 
      alt: 'Fox and Shadow Logo', 
      width: 800, 
      height: 600 
    },
    { 
      thumbnail: PCGLogoThumbnail || PCGLogo, 
      full: PCGLogo, 
      alt: 'Paper Cactus Games Logo', 
      width: 800, 
      height: 600 
    },
    { 
      thumbnail: PCGLogoLongThumbnail || PCGLogoLong, 
      full: PCGLogoLong, 
      alt: 'Paper Cactus Games Long Logo', 
      width: 800, 
      height: 200 
    },
  ];  
  
  return (
    <div className="logos-grid">
      {logoItems.map((item, index) => (
        <div key={index} className="logo-item">
          <div className="logo-container">
            <img
              onClick={() => handleImageClick(item)}
              src={item.thumbnail || item.full}
              alt={item.alt}
              className="logo-thumbnail"
            />
            <div className="logo-caption">{item.alt}</div>
            <div className="logo-instruction">Click to download high-resolution version</div>
          </div>
        </div>
      ))}      {selectedImage && (
        <div 
          className="custom-lightbox fade-in" 
          onClick={handleClose}
          onWheel={handleWheel}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div className="custom-lightbox-content" onClick={(e) => e.stopPropagation()}>
            {logoItems.length > 1 && (
              <>
                <button className="nav-button prev-button" onClick={(e) => {
                  e.stopPropagation();
                  const currentIndex = logoItems.findIndex(item => item.full === selectedImage.full);
                  const newIndex = currentIndex > 0 ? currentIndex - 1 : logoItems.length - 1;
                  handleImageClick(logoItems[newIndex]);
                }}>
                  <span>&lt;</span>
                </button>
                <button className="nav-button next-button" onClick={(e) => {
                  e.stopPropagation();
                  const currentIndex = logoItems.findIndex(item => item.full === selectedImage.full);
                  const newIndex = currentIndex < logoItems.length - 1 ? currentIndex + 1 : 0;
                  handleImageClick(logoItems[newIndex]);
                }}>
                  <span>&gt;</span>
                </button>
              </>
            )}            <div 
              className="custom-lightbox-image-container" 
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onDoubleClick={handleDoubleClick}
              onClick={(e) => e.stopPropagation()}
            >
              {isLoading && (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                </div>
              )}
              <img
                src={selectedImage.full}
                alt={selectedImage.alt}
                className={`custom-lightbox-image ${isLoading ? 'loading' : 'loaded'}`}
                onClick={handleImageToggleZoom}
                onLoad={() => setIsLoading(false)}
                style={{
                  transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
                  cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in'
                }}
              />
            </div>
            <div className="custom-lightbox-controls">
              <div className="custom-lightbox-info">
                <h3>{selectedImage.alt}</h3>
                <p>{selectedImage.width} x {selectedImage.height}px</p>
              </div>
              <div className="custom-lightbox-buttons">
                <button className="download-btn" onClick={handleDownload}>Download</button>
                <button className="close-btn" onClick={handleClose}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FnSpressKitLogos;